import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Filter, Plus, Package, RefreshCw, RotateCcw, Archive, Upload, Layers, BarChart3, Info, Pencil, Eye, ArrowUp, Loader2, Sparkles, Building2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import CreatePartModal from '../components/CreatePartModal';
import EditPartModal from '../components/EditPartModal';
import DeletePartModal from '../components/DeletePartModal';
import { BulkImportModalV2 } from '../components/bulk-import';
import { useBulkImport } from '../contexts/BulkImportContext';
import FilterPanel from '../components/FilterPanel';
import DashboardCharts from '../components/DashboardCharts';
import FilterChips from '../components/FilterChips';
import { PartOverviewContainer } from '../components/drawer';
import { usePartDrawer } from '../hooks/usePartDrawer';
import PartInfoTooltip from '../components/PartInfoTooltip';
import PartInfoModal from '../components/PartInfoModal';
import { reclassifyPart } from '../services/aiClassificationService';
import HoverTooltipWrapper from '../components/HoverTooltipWrapper';
import MainGroupsTooltip from '../components/MainGroupsTooltip';
import SubGroupsTooltip from '../components/SubGroupsTooltip';
import ManufacturersTooltip from '../components/ManufacturersTooltip';
import { usePartEvents } from '../contexts/WebSocketContext';
import ActivityFeed from '../components/ActivityFeed';

const PartsListPage = () => {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [taxonomyStats, setTaxonomyStats] = useState(null);
  const [mainGroups, setMainGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [restoringPart, setRestoringPart] = useState(null);
  
  // Bulk Import Modal state - using context
  const { isModalOpen: isBulkImportOpen, openModal: openBulkImport, closeModal: closeBulkImport } = useBulkImport();
  
  // Edit Part Modal state - for direct edit from table
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  
  // Lookup maps for group names
  const [mainGroupNames, setMainGroupNames] = useState({});
  const [subGroupNames, setSubGroupNames] = useState({});
  
  // New state for filter panel and charts
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showCharts, setShowCharts] = useState(true);
  const [chartsPopoverOpen, setChartsPopoverOpen] = useState(false); // Charts hover popover
  const [chartsPopoverVisible, setChartsPopoverVisible] = useState(false); // For fade animation
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    mainGroups: [],
    mainGroup: '',
    subGroups: [],
    status: '',
    manufacturers: []
  });
  
  // Right panel tab state: 'filters' or 'activity'
  const [rightPanelTab, setRightPanelTab] = useState('filters');
  
  // ===========================================
  // HYBRID SCROLL STATE - Per PRD Specification
  // ===========================================
  const SCROLL_CONFIG = {
    initialLoad: 50,        // Initial number of items to fetch
    batchSize: 50,          // Items per subsequent fetch
    loadTrigger: 0.80,      // Load next batch at 80% scroll
    skeletonCount: 5        // Skeleton rows during loading
  };
  
  const [totalParts, setTotalParts] = useState(0);           // Total matching parts from API
  const [hasMore, setHasMore] = useState(true);              // More items available to load
  const [loadingMore, setLoadingMore] = useState(false);     // Loading next batch
  const [loadError, setLoadError] = useState(null);          // Error state for retry
  const [viewingRange, setViewingRange] = useState({ start: 1, end: 0 }); // Currently viewing range
  const [showBackToTop, setShowBackToTop] = useState(false); // Back to top button visibility
  const [scrollPercentage, setScrollPercentage] = useState(0); // Scroll position percentage (0-100)
  
  const tableContainerRef = useRef(null);                    // Reference to scrollable table container
  const mainContentRef = useRef(null);                       // Reference to main content scroll container
  const chartsPopoverRef = useRef(null);                     // Reference to charts popover container
  const chartsButtonRef = useRef(null);                      // Reference to charts button
  const chartsCloseTimeoutRef = useRef(null);                // Timeout ref for delayed close
  const isLoadingRef = useRef(false);                        // Prevent duplicate load requests
  
  const POPOVER_CLOSE_DELAY = 800; // 800ms delay before closing
  // ===========================================
  
  // Check screen size for responsive behavior
  // isDesktop (>=1440px): Persistent filter sidebar on left
  // Below 1440px: Filter button + drawer
  // isMobile (<768px): Compact part numbers, hide UOM column
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1440);
  const [isLaptop, setIsLaptop] = useState(window.innerWidth >= 1024 && window.innerWidth < 1440);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Part Overview Drawer - POV-0040/POV-0042
  const { openDrawer } = usePartDrawer();
  
  // Part Info Tooltip/Modal state
  const [hoveredPartHpn, setHoveredPartHpn] = useState(null);
  const [infoModalPart, setInfoModalPart] = useState(null);
  const [reclassifyingPart, setReclassifyingPart] = useState(null);
  const tooltipTimeoutRef = useRef(null);
  
  // Delete/Archive Modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [partToDelete, setPartToDelete] = useState(null);

  // WebSocket: Auto-refresh when parts are created/updated/deleted by other users
  const handlePartEvent = useCallback((event) => {
    // Refresh parts list and stats when we receive a part event
    // (but not if it's from our own creation - that's handled by handlePartCreated)
    console.log('[PartsListPage] Real-time part event received:', event.event_type);
    loadParts();
    refreshAllStats();
  }, []);
  
  usePartEvents(handlePartEvent);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1440);
      setIsLaptop(window.innerWidth >= 1024 && window.innerWidth < 1440);
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initial data load - runs once on mount
  useEffect(() => {
    refreshAllStats();
    loadMainGroups();
  }, []);

  // Load parts when filters change
  useEffect(() => {
    loadParts();
  }, [selectedGroup, selectedStatus, selectedFilters.mainGroups, selectedFilters.subGroups, selectedFilters.manufacturers]);

  useEffect(() => {
    // Load subgroups when main group filter changes
    if (selectedFilters.mainGroups?.length > 0) {
      loadSubGroups(selectedFilters.mainGroups[0]);
    }
  }, [selectedFilters.mainGroups]);

  // Build query params for parts API
  const buildPartsParams = useCallback((skip = 0) => {
    const params = { 
      limit: SCROLL_CONFIG.batchSize,
      skip: skip
    };
    
    // Send all selected main groups as comma-separated
    if (selectedFilters.mainGroups?.length > 0) {
      params.main_group = selectedFilters.mainGroups.join(',');
    } else if (selectedGroup) {
      params.main_group = selectedGroup;
    }
    
    // Send all selected subgroups as comma-separated (composite keys: main_group-subgroup)
    if (selectedFilters.subGroups?.length > 0) {
      params.subgroup = selectedFilters.subGroups.join(',');
    }
    
    // Send all selected manufacturers as comma-separated
    if (selectedFilters.manufacturers?.length > 0) {
      params.manufacturer = selectedFilters.manufacturers.join(',');
    }
    
    if (searchQuery) params.q = searchQuery;
    if (selectedStatus) params.status = selectedStatus;
    
    return params;
  }, [selectedFilters, selectedGroup, searchQuery, selectedStatus]);

  // Initial load - resets everything and loads first batch
  const loadPartsRef = useRef(0);
  const loadParts = async () => {
    const callId = ++loadPartsRef.current;
    try {
      setLoading(true);
      setLoadError(null);
      setParts([]);
      setHasMore(true);
      
      const params = buildPartsParams(0);
      const response = await api.get('/api/v1/parts/search', { params });
      
      // Discard if a newer call has been initiated
      if (callId !== loadPartsRef.current) return;
      
      const items = response.data.items || [];
      const total = response.data.total || 0;
      const hasMoreItems = response.data.has_more || false;
      
      setParts(items);
      setTotalParts(total);
      setHasMore(hasMoreItems);
      setViewingRange({ start: items.length > 0 ? 1 : 0, end: items.length });
    } catch (error) {
      if (callId !== loadPartsRef.current) return;
      console.error('Failed to load parts:', error);
      setParts([]);
      setTotalParts(0);
      setHasMore(false);
      setLoadError('Failed to load parts. Please try again.');
    } finally {
      if (callId === loadPartsRef.current) setLoading(false);
    }
  };

  // Load more parts - appends to existing list (infinite scroll)
  const loadMoreParts = useCallback(async () => {
    // Prevent duplicate requests
    if (isLoadingRef.current || !hasMore || loadingMore) return;
    
    isLoadingRef.current = true;
    setLoadingMore(true);
    setLoadError(null);
    
    try {
      const params = buildPartsParams(parts.length);
      const response = await api.get('/api/v1/parts/search', { params });
      
      const newItems = response.data.items || [];
      const hasMoreItems = response.data.has_more || false;
      
      setParts(prevParts => {
        // Deduplicate: only add items not already in the list
        const existingIds = new Set(prevParts.map(p => p.hpn_compact));
        const uniqueNew = newItems.filter(p => !existingIds.has(p.hpn_compact));
        return uniqueNew.length > 0 ? [...prevParts, ...uniqueNew] : prevParts;
      });
      setHasMore(hasMoreItems);
    } catch (error) {
      console.error('Failed to load more parts:', error);
      setLoadError('Failed to load more parts. Please try again.');
    } finally {
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, [parts.length, hasMore, loadingMore, buildPartsParams]);

  // Scroll event handler - triggers load at 80% scroll (for desktop table scroll)
  const handleTableScroll = useCallback((e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Calculate scroll percentage for load trigger (based on bottom position)
    const loadTriggerPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    // Calculate scroll position percentage for the bidirectional bar (0-100%)
    // This represents how far we've scrolled through the scrollable content
    const maxScroll = scrollHeight - clientHeight;
    const currentScrollPercent = maxScroll > 0 ? Math.round((scrollTop / maxScroll) * 100) : 0;
    setScrollPercentage(currentScrollPercent);
    
    // Update viewing range based on scroll position
    const itemHeight = 52; // Approximate row height in pixels
    const startItem = Math.floor(scrollTop / itemHeight) + 1;
    const visibleItems = Math.ceil(clientHeight / itemHeight);
    const endItem = Math.min(startItem + visibleItems - 1, parts.length);
    setViewingRange({ start: Math.max(1, startItem), end: endItem });
    
    // Show/hide back to top button (after scrolling past ~50 items)
    setShowBackToTop(scrollTop > 50 * itemHeight);
    
    // Load more at 80% scroll threshold
    if (loadTriggerPercentage >= SCROLL_CONFIG.loadTrigger && hasMore && !loadingMore && !isLoadingRef.current) {
      loadMoreParts();
    }
  }, [parts.length, hasMore, loadingMore, loadMoreParts]);

  // Page-level scroll handler for mobile/iPad
  const handlePageScroll = useCallback((e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Calculate scroll percentage for load trigger
    const loadTriggerPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    // Calculate scroll position percentage
    const maxScroll = scrollHeight - clientHeight;
    const currentScrollPercent = maxScroll > 0 ? Math.round((scrollTop / maxScroll) * 100) : 0;
    setScrollPercentage(currentScrollPercent);
    
    // Estimate viewing range based on page scroll
    // Account for header content height (~400-500px)
    const headerHeight = 450;
    const tableScrollTop = Math.max(0, scrollTop - headerHeight);
    const itemHeight = 52;
    const startItem = Math.floor(tableScrollTop / itemHeight) + 1;
    const visibleItems = Math.ceil((clientHeight - 100) / itemHeight); // -100 for progress bar & footer
    const endItem = Math.min(startItem + visibleItems - 1, parts.length);
    setViewingRange({ 
      start: Math.max(1, Math.min(startItem, parts.length)), 
      end: Math.max(1, Math.min(endItem, parts.length))
    });
    
    // Show back to top after scrolling past header
    setShowBackToTop(scrollTop > 200);
    
    // Load more at 80% scroll threshold
    if (loadTriggerPercentage >= SCROLL_CONFIG.loadTrigger && hasMore && !loadingMore && !isLoadingRef.current) {
      loadMoreParts();
    }
  }, [parts.length, hasMore, loadingMore, loadMoreParts]);

  // Reference to the root scrollable container for mobile/iPad
  const rootContainerRef = useRef(null);

  // Scroll to top handler - adapts to device
  const scrollToTop = useCallback(() => {
    if (isDesktop) {
      tableContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      rootContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isDesktop]);

  // Scroll to bottom handler - adapts to device
  const scrollToBottom = useCallback(() => {
    if (isDesktop) {
      tableContainerRef.current?.scrollTo({ top: tableContainerRef.current.scrollHeight, behavior: 'smooth' });
    } else {
      rootContainerRef.current?.scrollTo({ top: rootContainerRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [isDesktop]);

  // Charts Popover Handlers - with delay and fade animation
  const openChartsPopover = useCallback(() => {
    // Clear any pending close timeout
    if (chartsCloseTimeoutRef.current) {
      clearTimeout(chartsCloseTimeoutRef.current);
      chartsCloseTimeoutRef.current = null;
    }
    setChartsPopoverOpen(true);
    setChartsPopoverVisible(true);
  }, []);

  const closeChartsPopover = useCallback(() => {
    // Clear any existing timeout
    if (chartsCloseTimeoutRef.current) {
      clearTimeout(chartsCloseTimeoutRef.current);
    }
    
    // Start delayed close with fade animation
    chartsCloseTimeoutRef.current = setTimeout(() => {
      setChartsPopoverVisible(false); // Start fade out
      // After fade animation completes, remove from DOM
      setTimeout(() => {
        setChartsPopoverOpen(false);
      }, 300); // 300ms fade duration
    }, POPOVER_CLOSE_DELAY);
  }, []);

  const handleChartsMouseEnter = useCallback(() => {
    openChartsPopover();
  }, [openChartsPopover]);

  const handleChartsMouseLeave = useCallback(() => {
    closeChartsPopover();
  }, [closeChartsPopover]);

  const handlePopoverMouseEnter = useCallback(() => {
    // Cancel close when entering popover
    if (chartsCloseTimeoutRef.current) {
      clearTimeout(chartsCloseTimeoutRef.current);
      chartsCloseTimeoutRef.current = null;
    }
    setChartsPopoverVisible(true);
  }, []);

  const handlePopoverMouseLeave = useCallback(() => {
    closeChartsPopover();
  }, [closeChartsPopover]);

  // Mobile: Toggle on click
  const handleChartsClick = useCallback(() => {
    if (chartsPopoverOpen) {
      setChartsPopoverVisible(false);
      setTimeout(() => setChartsPopoverOpen(false), 300);
    } else {
      openChartsPopover();
    }
  }, [chartsPopoverOpen, openChartsPopover]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (chartsCloseTimeoutRef.current) {
        clearTimeout(chartsCloseTimeoutRef.current);
      }
    };
  }, []);

  // Retry handler for load errors
  const handleRetry = useCallback(() => {
    setLoadError(null);
    if (parts.length === 0) {
      loadParts();
    } else {
      loadMoreParts();
    }
  }, [parts.length, loadMoreParts]);

  const loadStats = async () => {
    try {
      const response = await api.get('/api/v1/parts/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
      setStats({ total_parts: 0, active_parts: 0, archived_parts: 0 });
    }
  };

  const refreshAllStats = () => {
    loadStats();
    loadTaxonomyStats();
  };

  const loadMainGroups = async () => {
    try {
      const response = await api.get('/api/v1/taxonomy/main-groups');
      setMainGroups(response.data || []);
      // Build lookup map for main group names
      const names = {};
      (response.data || []).forEach(g => {
        names[g.code] = g.name;
      });
      setMainGroupNames(names);
      // Also load all subgroup names
      loadAllSubGroupNames(response.data || []);
    } catch (error) {
      console.error('Failed to load main groups:', error);
      setMainGroups([]);
    }
  };

  const loadAllSubGroupNames = async (groups) => {
    try {
      const allNames = {};
      for (const group of groups) {
        const response = await api.get(`/api/v1/taxonomy/subgroups/${group.code}`);
        (response.data || []).forEach(s => {
          allNames[`${s.main_group_code}:${s.subgroup_code}`] = s.name;
        });
      }
      setSubGroupNames(allNames);
    } catch (error) {
      console.error('Failed to load subgroup names:', error);
    }
  };

  const loadTaxonomyStats = async () => {
    try {
      const response = await api.get('/api/v1/taxonomy/stats');
      setTaxonomyStats(response.data);
      // Also set manufacturers from the same response (avoid duplicate API call)
      const mfrData = response.data.manufacturer_distribution || [];
      setManufacturers(mfrData);
    } catch (error) {
      console.error('Failed to load taxonomy stats:', error);
      setTaxonomyStats({ total_main_groups: 0, total_subgroups: 0 });
      setManufacturers([]);
    }
  };

  const loadSubGroups = async (mainGroupCode) => {
    try {
      const response = await api.get(`/api/v1/taxonomy/subgroups/${mainGroupCode}`);
      setSubGroups(response.data || []);
    } catch (error) {
      console.error('Failed to load subgroups:', error);
      setSubGroups([]);
    }
  };

  const handleFilterChange = (newFilters) => {
    setSelectedFilters(newFilters);
    // Sync with existing filter state
    if (newFilters.mainGroups?.length > 0) {
      setSelectedGroup(newFilters.mainGroups[0]);
    } else {
      setSelectedGroup('');
    }
    if (newFilters.status) {
      setSelectedStatus(newFilters.status);
    } else {
      setSelectedStatus('');
    }
  };

  const handleRemoveFilter = (type, code) => {
    if (type === 'mainGroup') {
      const newMainGroups = selectedFilters.mainGroups.filter(c => c !== code);
      handleFilterChange({
        ...selectedFilters,
        mainGroups: newMainGroups,
        mainGroup: newMainGroups[0] || '',
        subGroups: []
      });
    } else if (type === 'subGroup') {
      handleFilterChange({
        ...selectedFilters,
        subGroups: selectedFilters.subGroups.filter(c => c !== code)
      });
    } else if (type === 'status') {
      handleFilterChange({
        ...selectedFilters,
        status: ''
      });
    } else if (type === 'manufacturer') {
      handleFilterChange({
        ...selectedFilters,
        manufacturers: selectedFilters.manufacturers.filter(m => m !== code)
      });
    }
  };

  const handleMainGroupChartClick = (code) => {
    handleFilterChange({
      ...selectedFilters,
      mainGroups: [code],
      mainGroup: code,
      subGroups: []
    });
  };

  const handleSubGroupChartClick = (mainCode, subCode) => {
    handleFilterChange({
      ...selectedFilters,
      mainGroups: [mainCode],
      mainGroup: mainCode,
      subGroups: [subCode]
    });
  };

  const handleManufacturerChartClick = (name) => {
    handleFilterChange({
      ...selectedFilters,
      manufacturers: [name]
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadParts();
  };

  const handlePartCreated = (newPart) => {
    loadParts();
    refreshAllStats();
  };

  // Handle opening Edit Part modal directly from table
  const handleEditPart = async (hpnCompact, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    try {
      // Fetch full part data
      const response = await api.get(`/api/v1/parts/by-hpn/${hpnCompact}`);
      setEditingPart(response.data);
      setIsEditModalOpen(true);
    } catch (error) {
      console.error('Failed to load part for editing:', error);
      toast.error('Failed to load part details');
    }
  };

  // Handle successful edit
  const handleEditSuccess = (updatedPart) => {
    setIsEditModalOpen(false);
    setEditingPart(null);
    loadParts();
    refreshAllStats();
    toast.success('Part updated successfully');
  };

  const handleBulkImportSuccess = () => {
    loadParts();
    refreshAllStats();
  };

  const handleRestore = async (hpnCompact, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setRestoringPart(hpnCompact);
    try {
      await api.post(`/api/v1/parts/by-hpn/${hpnCompact}/restore`, {
        comment: 'Restored from parts list'
      });
      toast.success('Part restored successfully');
      loadParts();
      refreshAllStats();
    } catch (error) {
      console.error('Failed to restore part:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore part');
    } finally {
      setRestoringPart(null);
    }
  };

  const handleArchive = (hpnCompact, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Find the part to pass to the modal
    const part = parts.find(p => p.hpn_compact === hpnCompact);
    if (part) {
      setPartToDelete(part);
      setIsDeleteModalOpen(true);
    }
  };
  
  // Handle successful archive from DeletePartModal
  const handleArchiveSuccess = () => {
    loadParts();
    refreshAllStats();
    setIsDeleteModalOpen(false);
    setPartToDelete(null);
  };

  // Part Info Tooltip/Modal handlers
  const handleInfoMouseEnter = (part) => {
    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    // Delay showing tooltip to avoid flicker
    tooltipTimeoutRef.current = setTimeout(() => {
      setHoveredPartHpn(part.hpn_compact);
    }, 200);
  };

  const handleInfoMouseLeave = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    setHoveredPartHpn(null);
  };

  const handleInfoClick = (part, e) => {
    e.preventDefault();
    e.stopPropagation();
    setHoveredPartHpn(null);
    setInfoModalPart(part);
  };

  const handleCloseInfoModal = () => {
    setInfoModalPart(null);
  };

  const handleReclassifyPart = async (part) => {
    const hpnCompact = part?.hpn_compact || infoModalPart?.hpn_compact;
    if (!hpnCompact) return;
    
    setReclassifyingPart(hpnCompact);
    try {
      const result = await reclassifyPart(hpnCompact);
      if (result.success) {
        toast.success('Part classified successfully');
        // Update the part in local state with new classification data
        setParts(prevParts => prevParts.map(p => 
          p.hpn_compact === hpnCompact 
            ? { ...p, ai_classification_data: result.ai_classification_data }
            : p
        ));
        // Update the modal part if open
        if (infoModalPart?.hpn_compact === hpnCompact) {
          setInfoModalPart(prev => ({
            ...prev,
            ai_classification_data: result.ai_classification_data
          }));
        }
      } else {
        toast.error(result.error || 'Classification failed');
      }
    } catch (error) {
      console.error('Failed to reclassify part:', error);
      toast.error('Failed to run AI classification');
    } finally {
      setReclassifyingPart(null);
    }
  };

  // Helper to get formatted group display - full text on two lines
  const getGroupDisplay = (mainCode, subCode) => {
    const mainName = mainGroupNames[mainCode];
    const subName = subGroupNames[`${mainCode}:${subCode}`];
    
    return {
      mainName: mainName || mainCode,
      subName: subName || subCode,
      mainCode,
      subCode
    };
  };

  // Helper to get status border color
  const getStatusBorderColor = (status) => {
    switch (status) {
      case 'active': return 'border-l-green-500';
      case 'obsolete': return 'border-l-yellow-500';
      case 'deleted': return 'border-l-red-500';
      default: return 'border-l-slate-300';
    }
  };

  // Helper to get status tooltip text
  const getStatusTooltip = (status) => {
    switch (status) {
      case 'active': return 'Status: Active\nAvailable for ordering';
      case 'obsolete': return 'Status: Obsolete\nNo longer manufactured';
      case 'deleted': return 'Status: Archived\nHistorical record only';
      default: return `Status: ${status}`;
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedGroup('');
    setSelectedStatus('');
    setSelectedFilters({
      mainGroups: [],
      mainGroup: '',
      subGroups: [],
      status: '',
      manufacturers: []
    });
  };

  const activeFilterCount = 
    (selectedFilters.mainGroups?.length || 0) + 
    (selectedFilters.subGroups?.length || 0) + 
    (selectedFilters.status ? 1 : 0) +
    (selectedFilters.manufacturers?.length || 0);

  return (
    <div 
      ref={rootContainerRef}
      className={`h-full flex flex-col ${isDesktop ? 'overflow-hidden' : 'overflow-y-auto'}`}
      onScroll={!isDesktop ? handlePageScroll : undefined}
    >
      {/* Create Part Modal */}
      <CreatePartModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handlePartCreated}
      />

      {/* Edit Part Modal - Direct edit from table */}
      <EditPartModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingPart(null);
        }}
        onSuccess={handleEditSuccess}
        part={editingPart}
        mainGroups={mainGroups}
      />
      
      {/* Bulk Import Modal V2 */}
      <BulkImportModalV2
        isOpen={isBulkImportOpen}
        onClose={closeBulkImport}
        onSuccess={handleBulkImportSuccess}
      />
      
      {/* Delete/Archive Part Modal */}
      <DeletePartModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPartToDelete(null);
        }}
        part={partToDelete}
        onSuccess={handleArchiveSuccess}
      />

      {/* Main Layout: Content + Sidebar (desktop) */}
      <div 
        ref={mainContentRef}
        className={`flex gap-3 pl-4 sm:pl-6 lg:pl-8 pr-3 py-4 ${isDesktop ? 'flex-1 overflow-hidden' : ''}`}
      >
        
        {/* Main Content Area */}
        <div 
          className={`flex-1 min-w-0 flex flex-col ${isDesktop ? 'overflow-hidden' : ''}`}
        >
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Parts Catalogue</h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage and search your parts inventory
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex items-center space-x-3">
          {/* Charts Button with Hover Popover */}
          {taxonomyStats && (
            <div className="relative">
              <button
                ref={chartsButtonRef}
                className={`btn px-4 py-2 flex items-center space-x-2 border transition-colors ${
                  chartsPopoverOpen 
                    ? 'bg-slate-100 border-slate-300 text-slate-700' 
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                }`}
                data-testid="charts-button"
                onMouseEnter={handleChartsMouseEnter}
                onMouseLeave={handleChartsMouseLeave}
                onClick={handleChartsClick}
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Charts</span>
              </button>
              
              {/* Charts Hover Popover with fade animation */}
              {chartsPopoverOpen && (
                <div
                  ref={chartsPopoverRef}
                  className="absolute right-0 top-full z-50"
                  onMouseEnter={handlePopoverMouseEnter}
                  onMouseLeave={handlePopoverMouseLeave}
                  style={{
                    opacity: chartsPopoverVisible ? 1 : 0,
                    transition: 'opacity 300ms ease-out',
                    pointerEvents: chartsPopoverVisible ? 'auto' : 'none'
                  }}
                >
                  {/* Invisible hover bridge to connect button and popover */}
                  <div className="h-3 w-full" />
                  
                  {/* Popover content */}
                  <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-4 min-w-[480px]">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" />
                        Distribution Charts
                      </h3>
                      <span className="text-xs text-slate-400">Click pie to filter</span>
                    </div>
                    <DashboardCharts
                      taxonomyStats={taxonomyStats}
                      manufacturerStats={manufacturers}
                      selectedMainGroup={selectedFilters.mainGroups?.[0]}
                      onMainGroupClick={handleMainGroupChartClick}
                      onSubGroupClick={handleSubGroupChartClick}
                      onManufacturerClick={handleManufacturerChartClick}
                      showLegend={false}
                      mainGroupNames={mainGroupNames}
                      subGroupNames={subGroupNames}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          
          <button
            className="btn btn-secondary px-4 py-2 flex items-center space-x-2"
            data-testid="bulk-import-button"
            onClick={() => openBulkImport()}
          >
            <Upload className="w-4 h-4" />
            <span>Bulk Import</span>
          </button>
          <button
            className="btn btn-primary px-4 py-2 flex items-center space-x-2"
            data-testid="create-part-button"
            onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4" />
            <span>Create Part</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4 flex-shrink-0">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 group relative">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                Total Parts
                <Info className="w-3 h-3 text-slate-400 cursor-help" />
              </p>
              <p className="text-xl font-bold text-slate-900 numeric-value">
                {stats?.total_parts?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <div className="absolute left-0 top-full mt-1 w-48 p-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            Total number of parts in catalogue, including active and archived.
          </div>
        </div>
        
        <div 
          className={`bg-white rounded-xl shadow-sm border p-3 cursor-pointer transition-all group relative ${
            selectedStatus === 'active' 
              ? 'border-green-400 ring-2 ring-green-100' 
              : 'border-slate-200 hover:border-green-300'
          }`}
          onClick={() => {
            const newStatus = selectedStatus === 'active' ? '' : 'active';
            setSelectedStatus(newStatus);
            handleFilterChange({ ...selectedFilters, status: newStatus });
          }}
          data-testid="active-stats-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                Active
                <Info className="w-3 h-3 text-slate-400 cursor-help" />
              </p>
              <p className="text-xl font-bold text-green-600 numeric-value">
                {stats?.active_parts?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <div className="absolute left-0 top-full mt-1 w-48 p-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            Active parts available for use. Click to filter.
          </div>
        </div>
        
        <div 
          className={`bg-white rounded-xl shadow-sm border p-3 cursor-pointer transition-all group relative ${
            selectedStatus === 'deleted' 
              ? 'border-red-400 ring-2 ring-red-100' 
              : 'border-slate-200 hover:border-red-300'
          }`}
          onClick={() => {
            const newStatus = selectedStatus === 'deleted' ? '' : 'deleted';
            setSelectedStatus(newStatus);
            handleFilterChange({ ...selectedFilters, status: newStatus });
          }}
          data-testid="archived-stats-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                Archived
                <Info className="w-3 h-3 text-slate-400 cursor-help" />
              </p>
              <p className="text-xl font-bold text-red-600 numeric-value">
                {stats?.archived_parts?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Archive className="w-5 h-5 text-red-600" />
            </div>
          </div>
          <div className="absolute left-0 top-full mt-1 w-48 p-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            Archived/obsolete parts. Click to view.
          </div>
        </div>
        
        <HoverTooltipWrapper
          tooltip={
            <MainGroupsTooltip 
              taxonomyStats={taxonomyStats}
              onGroupClick={(code) => {
                handleFilterChange({ ...selectedFilters, mainGroup: code });
              }}
            />
          }
        >
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 hover:border-purple-300 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  Main Groups
                  <Info className="w-3 h-3 text-slate-400" />
                </p>
                <p className="text-xl font-bold text-purple-600 numeric-value">
                  {taxonomyStats?.total_main_groups || mainGroups.length}
                </p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Filter className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>
        </HoverTooltipWrapper>
        
        <HoverTooltipWrapper
          tooltip={
            <SubGroupsTooltip 
              onSubGroupClick={(mainCode, subCode) => {
                handleFilterChange({ ...selectedFilters, mainGroup: mainCode, subGroup: subCode });
              }}
            />
          }
        >
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 hover:border-orange-300 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  Sub Groups
                  <Info className="w-3 h-3 text-slate-400" />
                </p>
                <p className="text-xl font-bold text-orange-600 numeric-value">
                  {taxonomyStats?.total_subgroups || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Layers className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </div>
        </HoverTooltipWrapper>
        
        <HoverTooltipWrapper
          tooltip={
            <ManufacturersTooltip 
              manufacturers={manufacturers}
              onManufacturerClick={(name) => {
                handleFilterChange({ ...selectedFilters, manufacturer: name });
              }}
            />
          }
        >
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 hover:border-cyan-300 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  Manufacturers
                  <Info className="w-3 h-3 text-slate-400" />
                </p>
                <p className="text-xl font-bold text-cyan-600 numeric-value">
                  {taxonomyStats?.total_manufacturers || manufacturers?.length || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-cyan-600" />
              </div>
            </div>
          </div>
        </HoverTooltipWrapper>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-3 flex-shrink-0">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          {/* Filter Panel Toggle - visible only on screens < 1440px (non-desktop) */}
          {!isDesktop && (
            <button
              type="button"
              onClick={() => setShowFilterPanel(true)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                activeFilterCount > 0 
                  ? 'border-blue-400 bg-blue-50 text-blue-700' 
                  : 'border-slate-300 hover:bg-slate-50'
              }`}
              data-testid="filter-panel-toggle"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs font-medium rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          )}
          
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by HPN, description, or manufacturer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent"
              data-testid="parts-search-input"
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary px-6 py-2"
            data-testid="search-button"
          >
            Search
          </button>
          {(activeFilterCount > 0 || searchQuery) && (
            <button
              type="button"
              onClick={clearFilters}
              className="btn btn-secondary px-4 py-2 flex items-center gap-2"
              data-testid="clear-filters-button"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </form>
      </div>

      {/* Filter Chips */}
      {activeFilterCount > 0 && (
        <div className="mb-4">
          <FilterChips
            selectedFilters={selectedFilters}
            mainGroups={mainGroups}
            subGroups={subGroups}
            manufacturers={manufacturers}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={clearFilters}
          />
        </div>
      )}

      {/* Active Filter Notice */}
      {selectedStatus === 'active' && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Package className="w-5 h-5 text-green-500" />
            <span className="text-sm text-green-700">
              Showing <strong>active parts</strong> only.
            </span>
          </div>
          <button
            onClick={() => setSelectedStatus('')}
            className="text-sm text-green-600 hover:text-green-800 font-medium"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Archived Filter Notice */}
      {selectedStatus === 'deleted' && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Archive className="w-5 h-5 text-red-500" />
            <span className="text-sm text-red-700">
              Showing <strong>archived parts</strong> only. These parts are hidden from normal searches.
            </span>
          </div>
          <button
            onClick={() => setSelectedStatus('')}
            className="text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Show active parts
          </button>
        </div>
      )}

      {/* Hybrid Scroll Progress Bar - Sticky on mobile/iPad */}
      {parts.length > 0 && (
        <div className={`bg-white rounded-lg shadow-sm border border-slate-200 p-2 mb-2 flex-shrink-0 ${!isDesktop ? 'sticky top-0 z-20' : ''}`}>
          {/* Line 1: Viewing, Loaded info + Loaded Progress Bar (full remaining width) */}
          <div className="flex items-center text-sm gap-4">
            <div className="flex items-center gap-4 flex-shrink-0">
              <span className="text-slate-600">
                Viewing: <span className="font-semibold text-slate-800">{viewingRange.start}-{viewingRange.end}</span> of <span className="font-semibold text-slate-800">{totalParts.toLocaleString()}</span>
              </span>
              <span className="text-slate-400">│</span>
              <span className="text-slate-600">
                Loaded: <span className="font-semibold text-slate-800">{parts.length.toLocaleString()}</span>
                <span className="text-slate-400 ml-1">
                  ({totalParts > 0 ? Math.round((parts.length / totalParts) * 100) : 0}%)
                </span>
                {!hasMore && parts.length === totalParts && (
                  <span className="ml-2 text-green-600">✓</span>
                )}
              </span>
            </div>
            {/* Loaded Progress Bar - Full remaining width */}
            <div className="flex items-center gap-3 flex-1">
              {loadingMore && (
                <span className="flex items-center gap-2 text-blue-600 text-xs flex-shrink-0">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Loading...
                </span>
              )}
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300 ease-out rounded-full"
                  style={{ width: `${totalParts > 0 ? (parts.length / totalParts) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
          
          {/* Line 2: Scroll Position Bar (Bidirectional) - Same thickness as Line 1 */}
          <div className="mt-2 flex items-center gap-3">
            <span className="text-xs text-slate-500 flex-shrink-0 w-20">Scroll: {scrollPercentage}%</span>
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden relative">
              <div 
                className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-150 ease-out rounded-full"
                style={{ width: `${scrollPercentage}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Parts Table - Desktop: constrained scroll, Mobile/iPad: flows with page */}
      <div className={`bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col ${isDesktop ? 'overflow-hidden flex-1 min-h-0' : 'min-h-[300px]'}`}>
        <div 
          ref={tableContainerRef}
          className={`hide-scrollbar ${isDesktop ? 'overflow-y-auto flex-1' : ''}`}
          onScroll={isDesktop ? handleTableScroll : undefined}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <table className="w-full" data-testid="parts-table">
            {/* Table Header - Sticky only on desktop (inside scrolling table container) */}
            <thead className={`bg-slate-50 border-b border-slate-200 ${isDesktop ? 'sticky top-0 z-10' : ''}`}>
              <tr>
                <th className="w-1"></th>{/* Status border column */}
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider" style={{ width: '160px' }}>
                  Part Number
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Description
                </th>
                {/* UOM column - hidden on mobile (<768px) */}
                {!isMobile && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden lg:table-cell" style={{ width: '60px' }}>
                    UOM
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                // Skeleton loading
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="w-1"></td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-slate-200 rounded skeleton w-24 mb-1"></div>
                      <div className="h-3 bg-slate-100 rounded skeleton w-16"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-slate-200 rounded skeleton w-48 mb-1"></div>
                      <div className="h-3 bg-slate-100 rounded skeleton w-32"></div>
                    </td>
                    {!isMobile && (
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="h-4 bg-slate-200 rounded skeleton w-10"></div>
                      </td>
                    )}
                  </tr>
                ))
              ) : parts.length === 0 ? (
                <tr>
                  <td colSpan={isMobile ? "3" : "4"} className="px-4 py-12 text-center">
                    {selectedStatus === 'deleted' ? (
                      <>
                        <Archive className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500 font-medium">No archived parts</p>
                        <p className="text-slate-400 text-sm mt-1">
                          Archived parts will appear here
                        </p>
                      </>
                    ) : (
                      <>
                        <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500 font-medium">No parts found</p>
                        <p className="text-slate-400 text-sm mt-1">
                          Create your first part to get started
                        </p>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                parts.map((part) => {
                  const isArchived = part.status === 'deleted';
                  const isObsolete = part.status === 'obsolete';
                  // Mobile: Display compact part number (no spaces/dashes)
                  const displayPartNumber = isMobile 
                    ? part.hpn_compact 
                    : part.hpn_display;
                  return (
                    <tr 
                      key={part.hpn_compact} 
                      className={`transition-colors ${
                        isArchived 
                          ? 'bg-red-50/30 hover:bg-red-50/50' 
                          : isObsolete
                            ? 'bg-yellow-50/30 hover:bg-yellow-50/50'
                            : 'hover:bg-slate-50'
                      }`}
                      data-testid={`part-row-${part.hpn_compact}`}
                    >
                      {/* Status Border */}
                      <td 
                        className={`w-1 border-l-4 ${getStatusBorderColor(part.status)}`}
                        title={getStatusTooltip(part.status)}
                      ></td>
                      
                      {/* Part Number (Line 1) + Actions (Line 2) */}
                      <td className="px-4 py-2">
                        {/* Line 1: Part Number with Group Tooltip */}
                        <div className="group/hpn relative inline-block">
                          <span className={`hpn-display font-semibold text-sm cursor-help ${
                            isArchived ? 'text-red-400' : 'text-haden-primary'
                          }`}>
                            {displayPartNumber}
                          </span>
                          {/* Group Details Tooltip */}
                          <div className="absolute left-0 top-full mt-2 hidden group-hover/hpn:block z-50">
                            <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                              <div className="font-semibold mb-1">Group Details</div>
                              <div><span className="text-slate-400">Main Group:</span> {mainGroupNames[part.main_group_code] || part.main_group_code} ({part.main_group_code})</div>
                              <div><span className="text-slate-400">Subgroup:</span> {subGroupNames[`${part.main_group_code}:${part.subgroup_code}`] || part.subgroup_code} ({part.subgroup_code})</div>
                            </div>
                            <div className="absolute left-4 bottom-full w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-slate-800"></div>
                          </div>
                        </div>
                        
                        {/* Line 2: Action Icons */}
                        <div className="flex items-center space-x-0.5 mt-1">
                          {isArchived ? (
                            <>
                              {/* Restore button for archived parts */}
                              <button
                                onClick={(e) => handleRestore(part.hpn_compact, e)}
                                disabled={restoringPart === part.hpn_compact}
                                className="p-1 rounded text-green-600 hover:bg-green-50 hover:text-green-700 disabled:opacity-50 transition-colors"
                                data-testid={`restore-part-${part.hpn_compact}`}
                                title="Restore Part"
                              >
                                <RotateCcw className={`w-3.5 h-3.5 ${
                                  restoringPart === part.hpn_compact ? 'animate-spin' : ''
                                }`} />
                              </button>
                              {/* Archive (disabled for archived) */}
                              <button
                                disabled
                                className="p-1 rounded text-slate-300 cursor-not-allowed"
                                title="Already Archived"
                              >
                                <Archive className="w-3.5 h-3.5" />
                              </button>
                              {/* View button - opens drawer */}
                              <button
                                onClick={() => openDrawer(part.hpn_compact)}
                                className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                                data-testid={`view-archived-${part.hpn_compact}`}
                                title="View Details"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              {/* AI Classification Info button (archived) */}
                              <HoverTooltipWrapper
                                tooltip={
                                  <PartInfoTooltip
                                    part={part}
                                    classificationData={part.ai_classification_data}
                                    mainGroupNames={mainGroupNames}
                                    onReclassify={() => handleReclassifyPart(part)}
                                    isReclassifying={reclassifyingPart === part.hpn_compact}
                                  />
                                }
                                delay={200}
                                hideDelay={400}
                              >
                                <button
                                  onClick={(e) => handleInfoClick(part, e)}
                                  className={`p-1 rounded transition-colors ${
                                    part.ai_classification_data 
                                      ? 'text-blue-400 hover:bg-blue-50 hover:text-blue-500'
                                      : 'text-slate-300 hover:bg-slate-100 hover:text-slate-400'
                                  }`}
                                  data-testid={`info-archived-${part.hpn_compact}`}
                                  title=""
                                >
                                  {reclassifyingPart === part.hpn_compact ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              </HoverTooltipWrapper>
                            </>
                          ) : (
                            <>
                              {/* Edit button - Opens Edit Modal directly */}
                              <button
                                onClick={(e) => handleEditPart(part.hpn_compact, e)}
                                className="p-1 rounded text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                                data-testid={`edit-part-${part.hpn_compact}`}
                                title="Edit Part"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              {/* Archive button */}
                              <button
                                onClick={(e) => handleArchive(part.hpn_compact, e)}
                                className="p-1 rounded text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                                data-testid={`archive-part-${part.hpn_compact}`}
                                title="Archive Part"
                              >
                                <Archive className="w-3.5 h-3.5" />
                              </button>
                              {/* View button - opens drawer */}
                              <button
                                onClick={() => openDrawer(part.hpn_compact)}
                                className="p-1 rounded text-slate-500 hover:bg-slate-100 hover:text-haden-primary transition-colors"
                                data-testid={`view-part-${part.hpn_compact}`}
                                title="View Details"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              {/* AI Classification Info button */}
                              <HoverTooltipWrapper
                                tooltip={
                                  <PartInfoTooltip
                                    part={part}
                                    classificationData={part.ai_classification_data}
                                    mainGroupNames={mainGroupNames}
                                    onReclassify={() => handleReclassifyPart(part)}
                                    isReclassifying={reclassifyingPart === part.hpn_compact}
                                  />
                                }
                                delay={200}
                                hideDelay={400}
                              >
                                <button
                                  onClick={(e) => handleInfoClick(part, e)}
                                  className={`p-1 rounded transition-colors ${
                                    part.ai_classification_data 
                                      ? 'text-blue-500 hover:bg-blue-50 hover:text-blue-600'
                                      : 'text-slate-400 hover:bg-slate-100 hover:text-slate-500'
                                  }`}
                                  data-testid={`info-part-${part.hpn_compact}`}
                                  title=""
                                >
                                  {reclassifyingPart === part.hpn_compact ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              </HoverTooltipWrapper>
                            </>
                          )}
                        </div>
                      </td>
                      
                      {/* Description + Manufacturer • MPN */}
                      <td className="px-4 py-2">
                        <p className={`text-sm leading-snug ${
                          isArchived ? 'text-slate-500' : 'text-slate-900'
                        }`} title={part.description}>
                          {part.description}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {part.manufacturer_name || '—'}
                          <span className="mx-1.5">•</span>
                          {part.mpn || '—'}
                        </p>
                      </td>
                      
                      {/* UOM - hidden on mobile (<768px) */}
                      {!isMobile && (
                        <td className="px-4 py-2 hidden lg:table-cell">
                          <span className={`text-sm font-mono ${
                            isArchived ? 'text-slate-400' : 'text-slate-600'
                          }`}>
                            {part.uom || 'EA'}
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
              
              {/* Loading More Skeleton Rows */}
              {loadingMore && (
                [...Array(SCROLL_CONFIG.skeletonCount)].map((_, i) => (
                  <tr key={`skeleton-${i}`} className="animate-pulse">
                    <td className="w-1 border-l-4 border-l-slate-200"></td>
                    <td className="px-4 py-2">
                      <div className="h-4 bg-slate-200 rounded skeleton w-24 mb-1"></div>
                      <div className="h-3 bg-slate-100 rounded skeleton w-16"></div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="h-4 bg-slate-200 rounded skeleton w-48 mb-1"></div>
                      <div className="h-3 bg-slate-100 rounded skeleton w-32"></div>
                    </td>
                    {!isMobile && (
                      <td className="px-4 py-2 hidden lg:table-cell">
                        <div className="h-4 bg-slate-200 rounded skeleton w-10"></div>
                      </td>
                    )}
                  </tr>
                ))
              )}
              
              {/* Error State - Retry Row */}
              {loadError && !loadingMore && (
                <tr>
                  <td colSpan={isMobile ? "3" : "4"} className="px-4 py-6 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <p className="text-red-600 text-sm">{loadError}</p>
                      <button
                        onClick={handleRetry}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors"
                        data-testid="retry-load-button"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              
              {/* End of Results Row */}
              {!loading && !loadingMore && !hasMore && parts.length > 0 && parts.length === totalParts && (
                <tr>
                  <td colSpan={isMobile ? "3" : "4"} className="px-4 py-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
                      <span className="text-green-600">✓</span>
                      <span>End of results — All {totalParts.toLocaleString()} parts loaded</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Footer - Sticky bottom on mobile/iPad */}
        {parts.length > 0 && (
          <div className={`px-4 py-2 bg-slate-50 border-t border-slate-200 flex-shrink-0 ${!isDesktop ? 'sticky bottom-0 z-10' : ''}`}>
            <div className="flex items-center justify-between">
              {/* Left: Count info */}
              <p className="text-sm text-slate-500 flex-1">
                Showing <span className="font-medium">{parts.length.toLocaleString()}</span> of <span className="font-medium">{totalParts.toLocaleString()}</span> {selectedStatus === 'deleted' ? 'archived ' : ''}parts
              </p>
              
              {/* Center: Navigation Controls */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {scrollPercentage > 5 && (
                  <button
                    onClick={scrollToTop}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                    data-testid="go-to-top-button"
                  >
                    <ArrowUp className="w-3 h-3" />
                    <span>Top</span>
                  </button>
                )}
                {scrollPercentage < 95 && (
                  <button
                    onClick={scrollToBottom}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                    data-testid="go-to-bottom-button"
                  >
                    <span>Bottom</span>
                    <ArrowUp className="w-3 h-3 rotate-180" />
                  </button>
                )}
              </div>
              
              {/* Right: Status */}
              <div className="flex-1 text-right">
                {loadingMore ? (
                  <span className="flex items-center justify-end gap-2 text-xs text-blue-600">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Fetching...
                  </span>
                ) : hasMore ? (
                  <span className="text-xs text-slate-400">Scroll for more</span>
                ) : (
                  <span className="text-xs text-green-600">All loaded ✓</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

        </div>{/* End Main Content Area */}

        {/* Right Panel with Tabs - Desktop only (>=1440px) */}
        {isDesktop && (
          <div 
            className="w-[350px] flex-shrink-0 overflow-hidden h-full flex flex-col border border-slate-200 bg-white rounded-xl shadow-sm"
            data-testid="right-panel-desktop"
          >
            {/* Tab Header - with rounded top corners */}
            <div className="flex bg-slate-50 rounded-t-xl overflow-hidden border-b border-slate-200">
              <button
                onClick={() => setRightPanelTab('filters')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  rightPanelTab === 'filters'
                    ? 'text-haden-primary border-b-2 border-haden-primary bg-white -mb-[1px]'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100 border-b-2 border-transparent'
                }`}
                data-testid="tab-filters"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <button
                onClick={() => setRightPanelTab('activity')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  rightPanelTab === 'activity'
                    ? 'text-haden-primary border-b-2 border-haden-primary bg-white -mb-[1px]'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100 border-b-2 border-transparent'
                }`}
                data-testid="tab-activity"
              >
                <Clock className="w-4 h-4" />
                Activity
              </button>
            </div>
            
            {/* Tab Content - with rounded bottom corners */}
            <div className="flex-1 overflow-hidden rounded-b-xl">
              {rightPanelTab === 'filters' ? (
                <FilterPanel
                  isOpen={true}
                  onClose={() => {}}
                  selectedFilters={selectedFilters}
                  onFilterChange={handleFilterChange}
                  isDrawer={false}
                />
              ) : (
                <ActivityFeed />
              )}
            </div>
          </div>
        )}

      </div>{/* End Main Layout flex container */}

      {/* Filter Panel Drawer - for screens < 1440px (non-desktop) */}
      {!isDesktop && (
        <FilterPanel
          isOpen={showFilterPanel}
          onClose={() => setShowFilterPanel(false)}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          isDrawer={true}
        />
      )}

      {/* Part Overview Drawer */}
      <PartOverviewContainer />
      
      {/* Part Info Modal - AI Classification Details */}
      <PartInfoModal
        isOpen={!!infoModalPart}
        onClose={handleCloseInfoModal}
        part={infoModalPart}
        classificationData={infoModalPart?.ai_classification_data}
        onReclassify={() => handleReclassifyPart(infoModalPart)}
        isReclassifying={reclassifyingPart === infoModalPart?.hpn_compact}
      />
    </div>
  );
};

export default PartsListPage;
